/**
 * RBAC matrix for the XOP Internal Dashboard.
 *
 * Sections (top-nav tabs) and the permissions that gate them. The matrix is
 * the single source of truth — server enforces it, web reads the same file
 * to decide which tabs to render. If you change a permission name here, both
 * sides update at the same time.
 *
 * Permission keys follow `<section>:<verb>`:
 *   - `view`   — see the section / read your own data
 *   - `edit`   — mutate inside the section (within whatever scope you can view)
 *   - `manage` — system-admin scope inside the section (configure SLAs,
 *                tenant settings, integrations, etc.). NOT a data-scope
 *                widener. Reserved for `super_admin`. (The CRM does NOT use a
 *                `:manage` grant — its admin/config pages are gated by
 *                `admin:access`/super_admin, so a separate `crm:manage`
 *                checkbox would just be a dead toggle.)
 *
 * CRM **data scope** is a single binary on top of `crm:view`:
 *   - `crm:view`     — your OWN records only (assigned to / created by you).
 *   - `crm:view_all` — EVERY record in the tenant. It does NOT unlock new
 *                      pages; it only relaxes the row filter. (There is no
 *                      "team" tier — the command center owns users; the CRM has
 *                      no teams of its own.)
 *
 * Row-level filtering is enforced server-side by the brain CRM, which reads the
 * granted scope from the JWT (the `crm_view_all` claim) and tightens or relaxes
 * the SQL accordingly. Human-readable labels for every permission live in
 * `PERMISSION_LABELS` / `PERMISSION_DESCRIPTIONS` below (the roles matrix shows
 * them, so a checkbox is never a cryptic suffix).
 *
 * Two cross-cutting permissions live under `admin:*`:
 *   - `admin:users`        — invite, suspend, set roles
 *   - `admin:permissions`  — change role definitions / per-user grants
 *
 * The top-nav sections:
 *   dashboard · support · crm · sales · marketing · documentation · server · tester
 *
 * `tester` is the newest, restricted surface (rendered LAST, after
 * `server`). It's where an operator points a testing agent at one of our
 * servers and exercises its outbound capabilities — SMS, voice calls,
 * etc. It ships gated behind `tester:view`, granted to `super_admin` and
 * the dedicated `tester` role (added 2026-06-08); admins can opt *other*
 * roles in via the editable matrix (`/admin/users` → Roles). The actual
 * fire-the-test action is a separate `tester:run` primitive (see below) —
 * visibility and execution split so a role can browse the surface without
 * authority to send real messages. `tester:run` stays `super_admin`-only
 * for now (the `tester` role gets `view`, not `run`, while the execution
 * UI is under construction).
 *
 * Otto is intentionally NOT a top-level section. Managing OttoBots is a
 * system-administration concern, so it lives as a native tab inside
 * System Admin (`/admin/otto`) — gated by `admin:users`, same as the
 * rest of that area — not as a standalone nav entry.
 */

export const SECTIONS = [
  "dashboard",
  "support",
  "crm",
  "sales",
  "marketing",
  "documentation",
  "server",
  // Restricted capability-testing surface — super_admin only (see
  // ROLE_PERMISSIONS); sits near the end with the other utility tabs.
  "tester",
  // Internal staff team chat (DMs + an "All Staff" broadcast room). Rendered
  // LAST in the nav: the global presence rail (on every page) is the primary
  // entry point, so the tab is the secondary one. All signed-in staff carry
  // `chat:view`.
  "chat",
] as const;

export type Section = (typeof SECTIONS)[number];

export const PERMISSIONS = [
  "dashboard:view",

  // Internal staff team chat. A single `view` grant gates the section + its
  // nav tab; there is no edit/manage split — every viewer can DM any teammate
  // and post to the "All Staff" room. Granted to all three built-in roles.
  "chat:view",

  "support:view",
  "support:edit",
  "support:manage",

  "crm:view",
  "crm:view_all",
  "crm:edit",

  // ── Granular CRM page + data-class permissions (2026-06-09) ──────────
  // `crm:view` is the coarse gate: it lets a staff member ENTER the CRM and
  // call its read endpoints. These finer grants decide WHICH pages a role
  // actually sees inside the CRM and — critically — whether they may see any
  // monetary information at all. They are surfaced as individual toggles in the
  // editable roles matrix (`/admin/users` → Roles), so an admin can build, say,
  // a marketing role that sees customers + prospects but never a dollar figure.
  //
  // Page-scope grants (each maps to one CRM SPA `<page>:access` string in
  // server/src/auth/crmSessionPermissions.ts):
  "crm:tasks",
  "crm:prospects",
  "crm:customers",
  "crm:contacts",
  "crm:file_manager",
  "crm:resellers",
  // Reseller WRITE (2026-10-02): create / edit / delete reseller companies
  // (within the data scope — an own-records holder only the resellers it is
  // attached to). It used to be DERIVED from `crm:resellers` + `crm:edit`; the
  // CEO wanted it switchable per role and per person, so it is a grant of its
  // own now. Needs `crm:resellers` too (you cannot edit what the wall hides).
  // sales_manager baseline. Rides to crm-api as the `crm_resellers_edit` claim.
  "crm:resellers_edit",
  // Reseller SALESPEOPLE (2026-10-02): attach / remove OUR salespeople on a
  // reseller (the profile's Assigned Salespeople card, the salespeople picker
  // in the reseller form). Deciding who looks after a reseller used to be
  // super_admin-only; this lets an admin hand it to one person (e.g. a sales
  // lead) without making them a super admin. Same data scope as everything
  // else. NOT in any non-admin baseline. Bulk assignment across many resellers
  // stays super_admin-only. Rides to crm-api as `crm_resellers_salespeople`,
  // read strictly (absent ⇒ refused).
  "crm:resellers_salespeople",
  // Reseller APPLICATIONS (2026-09-12): the queue of companies asking to BECOME
  // resellers, reviewed on the Reseller Applications tab of CRM ▸ Reseller
  // Management (list, review, countersign, Activate / Reject). Its own grant on
  // top of `crm:resellers`, because deciding who joins the reseller network is
  // a narrower job than working with the resellers that exist. super_admin
  // baseline only — no other built-in role holds it; an admin grants it per
  // role in the matrix. Rides to crm-api as the `crm_reseller_applications`
  // claim, read strictly (absent ⇒ refused).
  "crm:reseller_applications",
  // The Licences page: build a licence for one appliance and issue the signed
  // file. Issuing needs `crm:edit` on top of this — reading a licence and
  // minting one a customer installs are different acts.
  "crm:licences",
  // NOTE: there is deliberately NO `crm:licences_approve` grant. Approving a
  // licence longer than a 30-day trial is a super_admin act and nothing else
  // (CEO, 2026-09-11) — see `canApproveCrmLicences`, which mints the
  // `crm_licences_approve` claim crm-api enforces on its own.
  // CRM ▸ Products & Pricing (2026-09-12): the product catalogue (Value Added
  // Services, Platform Extensions, support items), the release timeline with
  // each version's change log + Disk 1 / Disk 2 images and the download
  // report, and the XOP price list — the two pages that used to be System
  // Admin tabs (`/admin/products-releases`, `/admin/price-list`). One grant
  // opens the whole page READ-ONLY; curating any of it (adding a product,
  // recording where a disk image lives, repricing an item) still needs
  // `admin:users`, and taking a disk image still needs
  // `releases:download_images`. super_admin baseline only — an admin grants
  // it to sales or anyone else per role in the matrix when the time comes.
  "crm:products_pricing",
  // The financial wall. Gates the money-centric PAGES (Invoices, Quotes,
  // Financial Metrics) AND every monetary field rendered on any OTHER page
  // (customer MRR/LTV, prospect deal value, agreement/equipment prices, billing
  // tabs, scheduled-invoice amounts, revenue dashboards). Absent ⇒ the CRM SPA
  // redacts money everywhere and the financial pages are removed from nav AND
  // route-guarded; the minted CRM token also carries `crm_financials:false` so
  // crm-api can omit the fields server-side. There is no "see invoices but not
  // other money" split — financial visibility is one all-or-nothing class so the
  // wall can never be half-open.
  "crm:financials",

  "sales:view",
  "sales:edit",
  "sales:manage",

  "marketing:view",
  "marketing:edit",
  "marketing:manage",

  "documentation:view",
  // `create` is the middle authoring tier between `view` and `edit`: it lets a
  // user create NEW documents and edit/delete/publish ONLY the documents they
  // authored. It does NOT grant category (folder) management — that stays
  // `edit`-tier. `edit` is the full tier (CRUD on ANY doc + categories) and
  // implies every `create` power. Granted to sales_manager so sales staff can
  // self-author collateral without touching anyone else's docs.
  "documentation:create",
  "documentation:edit",
  // Who SEES which documents, per customer and per reseller. A separate
  // permission from `documentation:edit` because it is a different job:
  // `edit` is authority over the LIBRARY (write a doc, move a folder),
  // this is authority over ENTITLEMENT (which account reads what). A docs
  // author should be able to write without deciding what a customer has
  // bought, and whoever decides that need not be able to rewrite the doc.
  "documentation:manage_access",

  "server:view",
  "server:manage",

  // Capability-testing surface. `view` gates the page + nav tab; `run`
  // gates actually firing a test (sending real SMS / placing real voice
  // calls / etc. against a target server). They're split so a role can be
  // given read-only visibility without authority to trigger live traffic.
  // `tester:run` gates the live runner: registering / editing a target
  // server (with its API keys) AND firing a real test (placing actual calls /
  // sending real SMS). Carried only by super_admin out of the box; the
  // dedicated `tester` role gets `tester:view` (browse + history) only.
  "tester:view",
  "tester:run",

  // ── Release downloads ────────────────────────────────────────────────
  // A release ships two things people take away: the release-notes document,
  // and the installer disk images. The NOTES are public to every audience —
  // customers and resellers read them in their own portals, and no dashboard
  // permission gates that.
  //
  // The IMAGES are not. `releases:download_images` is who, among STAFF, may
  // pull disk1/disk2 — super admins and sales, the people who hand them to a
  // reseller. It rides to portal-api as the `act_as_release_images` claim,
  // where it is read strictly (absent ⇒ denied), and every attempt it permits
  // or refuses is written to the download log.
  //
  // It is deliberately NOT an `admin:*` key: curating the release catalogue is
  // System-Admin work (`admin:users`), while taking an image is a different
  // authority held by people who have no business in System Admin at all. A
  // sales manager holds this and nothing else under `admin:`.
  "releases:download_images",

  "admin:users",
  "admin:permissions",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Built-in roles. Five roles — no generic "admin", "viewer", or a separate
 * "sales_rep". Each role is a baseline bundle of section grants; admins
 * layer per-role + per-user overrides on top via `/admin/users` → Roles.
 *
 *   - `super_admin`       → everything (ALL data, every page, admin surface)
 *   - `support_agent`     → dashboard + support tickets + docs
 *   - `sales_manager`     → ALL CRM pages incl. financials, **own records only**,
 *                           + sales + marketing
 *   - `marketing_manager` → dashboard + marketing (view/edit) + a SCOPED CRM tab:
 *                           Customers + Prospects, **ALL records** (`crm:view_all`),
 *                           read-only, **NO financials** + docs + chat
 *   - `tester`            → servers + the Tester surface (`tester:view`) + docs
 *                           + chat. View-only on Tester: no `tester:run`, so it
 *                           can browse the surface but not fire live SMS/voice.
 *
 * (`marketing_manager` + `tester` were added 2026-06-08; marketing gained its
 * scoped CRM access 2026-06-09. The sales role covers marketing too — sales and
 * marketing share one combined surface for `sales_manager`.)
 *
 * CRM data scope is the deliberate contrast between the two data roles:
 *
 *   - sales_manager  → `crm:view`     — sees EVERY CRM page, but only the records
 *                       **assigned to / created by them** (own-records-only).
 *   - marketing_manager → `crm:view` + `crm:view_all` — sees only TWO pages
 *                       (Customers + Prospects), but **ALL** records on them, and
 *                       never a dollar figure (no `crm:financials`).
 *   - super_admin    → `crm:view` + `crm:view_all` (every record, every page).
 *   - the rest       → no CRM tab at all (no `crm:view`).
 *
 * There is NO team tier and NO `crm:manage` checkbox — the command center owns
 * users (the CRM has no teams), and CRM admin/config pages are gated by
 * `admin:access`/super_admin, so neither would be anything but a dead toggle.
 */
export const ROLES = [
  "super_admin",
  "support_agent",
  "sales_manager",
  "marketing_manager",
  "tester",
] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  super_admin: PERMISSIONS,
  support_agent: [
    "dashboard:view",
    "chat:view",
    "support:view",
    "support:edit",
    "documentation:view",
  ],
  // Sales + Marketing combined. ALL CRM pages (incl. financials + resellers),
  // but **own records only** (`crm:view`, NOT `crm:view_all`), so the brain CRM
  // keeps the "records assigned to / created by you" row filter. A sales manager
  // sees every section but only their own deals.
  // NO `dashboard:view`: a sales role works entirely inside CRM / Sales /
  // Marketing, so the company-pulse dashboard is removed for them — they land
  // on CRM (their first visible section) via the index `LandingRoute`.
  // super_admin + support_agent keep the dashboard.
  sales_manager: [
    "chat:view",
    "crm:view",
    "crm:edit",
    // Every CRM page — including financials (invoices, quotes, agreement values,
    // revenue dashboards) and resellers — a sales manager works all of them.
    "crm:tasks",
    "crm:prospects",
    "crm:customers",
    "crm:contacts",
    "crm:file_manager",
    "crm:resellers",
    // Creates resellers + edits the ones it is attached to (2026-10-02: its own
    // grant now, was crm:resellers + crm:edit). NOT crm:resellers_salespeople —
    // choosing who looks after a reseller is granted per person.
    "crm:resellers_edit",
    "crm:licences",
    "crm:financials",
    "sales:view",
    "sales:edit",
    "marketing:view",
    "marketing:edit",
    "documentation:view",
    // Author tier: sales staff can create docs + edit/delete/publish their own
    // (own-records-only, like their CRM scope). NOT `documentation:edit` — they
    // can't touch docs other people authored, nor manage category folders.
    "documentation:create",
    // Sales hands installer images to resellers, so sales can pull them. This
    // is the only grant a sales manager holds outside CRM / Sales / Marketing /
    // Docs, and it unlocks a download, not a page.
    "releases:download_images",
  ],
  // Marketing-focused role: the company-pulse dashboard + the Marketing section
  // (view AND edit — steer the AI agent, author/send campaigns) + a SCOPED CRM
  // tab + docs (read) + chat.
  // CRM scope is the deliberate inverse of sales: only TWO pages (Customers +
  // Prospects) but **ALL records** on them (`crm:view`, *and* `crm:view_all` so
  // they see everyone's customers/prospects, not just their own). Read-only (no
  // `crm:edit`) and **NO `crm:financials`** — so a marketing manager can see
  // every customer and prospect but never a single dollar figure anywhere in the
  // CRM (the financial wall holds on every page, not just by hiding Invoices).
  // (Side effect: with `crm:view` the Marketing ▸ Email Campaigns sub-tab, which
  // needs a CRM session, now works for them too.) Keeps `dashboard:view` (unlike
  // sales_manager) — they DO land on the company pulse.
  marketing_manager: [
    "dashboard:view",
    "chat:view",
    "marketing:view",
    "marketing:edit",
    "crm:view",
    "crm:view_all",
    "crm:customers",
    "crm:prospects",
    "documentation:view",
  ],
  // Capability-testing role: the Servers page + the Tester surface
  // (`tester:view` only — browse/inspect, no `tester:run`, so no authority to
  // fire live SMS/voice while the execution UI is under construction) + docs
  // (read) + chat. No `dashboard:view`, so it lands on its first visible
  // section (Documentation) via the web LandingRoute.
  tester: [
    "chat:view",
    "server:view",
    "documentation:view",
    "tester:view",
  ],
};

/**
 * Which permission gates the *visibility* of each top-nav tab. Edits and
 * deeper actions check additional permissions inside the section.
 */
export const SECTION_VIEW_PERMISSION: Record<Section, Permission> = {
  dashboard: "dashboard:view",
  chat: "chat:view",
  support: "support:view",
  crm: "crm:view",
  sales: "sales:view",
  marketing: "marketing:view",
  documentation: "documentation:view",
  server: "server:view",
  tester: "tester:view",
};

/**
 * Pretty labels for sections — kept here so server-side audit logs and the
 * web UI agree on the human-readable name of each section.
 */
export const SECTION_LABELS: Record<Section, string> = {
  dashboard: "Dashboard",
  chat: "Chat",
  support: "Support",
  crm: "CRM",
  sales: "Sales",
  marketing: "Marketing",
  // The user-facing name since 2026-09-14. The KEY stays `documentation`:
  // it doubles as the permission prefix and the route prefix, and both are
  // wire contracts (JWT claims, stored role overrides, deep links).
  documentation: "Documentation",
  server: "Servers",
  tester: "Tester",
};

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: "Super Admin",
  support_agent: "Support Agent",
  sales_manager: "Sales Manager",
  marketing_manager: "Marketing Manager",
  tester: "Tester",
};

/**
 * Human-readable name for every permission — what the roles matrix shows on a
 * checkbox instead of the cryptic `<section>:<verb>` suffix. Keep one entry per
 * PERMISSION (pinned by permissions.test.ts) so a toggle is never a mystery.
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
  "dashboard:view": "View the company dashboard",
  "chat:view": "Use staff chat",
  "support:view": "View support tickets",
  "support:edit": "Work support tickets",
  "support:manage": "Manage support settings",
  "crm:view": "Open the CRM (own records)",
  "crm:view_all": "See all records (not just your own)",
  "crm:edit": "Create / edit CRM records",
  "crm:tasks": "Show the Tasks page",
  "crm:prospects": "Show the Prospects pages",
  "crm:customers": "Show the Customers pages",
  "crm:contacts": "Show the Contacts pages",
  "crm:file_manager": "Show the File Manager",
  "crm:licences": "Show the Licences page",
  "crm:products_pricing": "Show the Products & Pricing page (catalogue, releases, price list)",
  "crm:resellers": "Show the Resellers page",
  "crm:resellers_edit": "Create / edit resellers",
  "crm:resellers_salespeople": "Attach / remove salespeople on resellers",
  "crm:reseller_applications": "Show the Reseller Applications tab (review who joins)",
  "crm:financials": "See financial info (invoices, quotes, purchase orders, revenue, all $ amounts)",
  "sales:view": "View the Sales agent",
  "sales:edit": "Steer the Sales agent",
  "sales:manage": "Manage Sales settings",
  "marketing:view": "View Marketing",
  "marketing:edit": "Run Marketing (steer agent, send campaigns)",
  "marketing:manage": "Manage Marketing settings",
  "documentation:view": "Read the documentation",
  "documentation:create": "Author articles (edit your own only)",
  "documentation:edit": "Edit any article + manage folders",
  "documentation:manage_access": "Set which customers + resellers see which articles",
  "server:view": "View server status",
  "server:manage": "Manage servers",
  "tester:view": "Open the Tester surface",
  "tester:run": "Configure servers + fire live tests (real SMS / voice)",
  "releases:download_images": "Download release disk images (Disk 1 / Disk 2)",
  "admin:users": "Manage users (invite, suspend, set roles)",
  "admin:permissions": "Edit role / permission definitions",
};

/**
 * One-line explanation per permission — shown as helper text / tooltip in the
 * roles matrix so an admin knows exactly what a toggle does.
 */
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  "dashboard:view": "See the cross-company pulse / landing dashboard.",
  "chat:view": "Direct-message teammates and post to the All-Staff room.",
  "support:view": "Read the support ticket queue and conversations.",
  "support:edit": "Reply to, assign, and resolve support tickets.",
  "support:manage": "Configure support settings (SLAs, etc.). Super-admin scope.",
  "crm:view": "Enter the CRM and read your OWN records (assigned to / created by you). Required for any CRM access.",
  "crm:view_all": "Widen CRM visibility from own-records-only to EVERY record in the tenant. Does not unlock pages — only relaxes the row filter.",
  "crm:edit": "Create and edit CRM records you can see.",
  "crm:tasks": "Show the CRM Tasks page in the nav + route.",
  "crm:prospects": "Show the Prospects and AI Prospects pages.",
  "crm:customers": "Show the Customers pages (list, detail, world map).",
  "crm:contacts": "Show the Contacts and Contact Groups pages.",
  "crm:file_manager": "Show the CRM File Manager.",
  "crm:licences":
    "Show the CRM Licences page. Build a licence for one appliance and issue the " +
    "signed file a customer installs. Issuing also needs \"Create / edit CRM records\".",
  "crm:products_pricing":
    "Show CRM \u25b8 Products & Pricing: the product catalogue (Value Added Services, " +
    "Platform Extensions, support), every release with its change log and Disk 1 / Disk 2 " +
    "images, the download report, and the XOP price list. Read-only on its own — curating " +
    "the catalogue or repricing needs \"Manage users\", downloading a disk image needs " +
    "\"Download release disk images\". Super admins only by default.",
  "crm:resellers": "Show the Resellers page.",
  "crm:resellers_edit":
    "Add new resellers and edit, deactivate or delete existing ones (with \"See all records\" any reseller, otherwise only the ones you are attached to). Needs Show the Resellers page too.",
  "crm:resellers_salespeople":
    "Decide which of our salespeople look after a reseller: attach and remove them on the reseller profile and in the reseller form. Follows the same record scope. Needs Show the Resellers page too. Super admins only by default.",
  "crm:reseller_applications":
    "Show the Reseller Applications tab on CRM \u25b8 Reseller Management and review the companies asking to become resellers: countersign their documents, Activate or Reject. Needs Show the Resellers page too. Super admins only by default.",
  "crm:financials": "Unlock the Invoices, Quotes, Purchase Orders and Financial-Metrics pages AND every monetary field on every other CRM page. Without it, all money is redacted everywhere — not just the Invoices page hidden.",
  "sales:view": "Watch the autonomous sales agent's work.",
  "sales:edit": "Steer the sales agent (mode, pause/resume, directives).",
  "sales:manage": "Configure sales-agent settings. Super-admin scope.",
  "marketing:view": "Watch the marketing AI agent.",
  "marketing:edit": "Steer the marketing agent and author / send email campaigns.",
  "marketing:manage":
    "Configure marketing settings — connect/disconnect the social accounts the agent posts to and flip the master autonomous-publishing switch. Distinct from marketing:edit (which only steers the agent + sends campaigns).",
  "documentation:view": "Read the documentation.",
  "documentation:create": "Create new articles and edit/delete/publish only the ones you authored.",
  "documentation:edit": "Edit/delete/publish ANY article and manage the documentation folders.",
  "documentation:manage_access":
    "Open Documentation \u25b8 Access (or a reseller profile's Documentation tab) and decide, per customer and per reseller, which articles and folders that account can read. Does not grant editing the articles themselves.",
  "server:view": "See server health / status.",
  "server:manage": "Manage server configuration. Super-admin scope.",
  "tester:view": "Open the capability-testing surface (read-only).",
  "tester:run": "Configure test target servers and fire live tests — sends real SMS / places real voice calls.",
  "releases:download_images":
    "Download the Disk 1 / Disk 2 installer images attached to a release. Release NOTES are readable by everyone (customers and resellers included) and need no permission; the images do. Every download attempt is recorded, granted or refused.",
  "admin:users": "Invite, suspend and set roles for dashboard users.",
  "admin:permissions": "Change what each role / user is allowed to do.",
};

/**
 * Resolve a role to its permission set. Wrapped in a function rather than
 * exposed as a Map so callers stay framework-agnostic and can't mutate the
 * shared matrix by accident.
 */
export function permissionsForRole(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}

/**
 * Permissions a role may NEVER hold — even via an admin override. The sales
 * role works entirely inside CRM / Sales / Marketing; the company-pulse
 * dashboard has nothing it can act on, so `sales_manager` is barred from
 * `dashboard:view` and always lands on CRM (the web `LandingRoute`). Enforced
 * where effective permissions are resolved (server `RolePermissionOverridesRepo`
 * — strips it from baseline OR override) and mirrored in the editable roles
 * matrix (the toggle is disabled), so a saved override can't re-grant it and
 * the matrix never lies.
 */
export const ROLE_FORBIDDEN_PERMISSIONS: Partial<
  Record<Role, readonly Permission[]>
> = {
  sales_manager: ["dashboard:view"],
};

/** True when `role` is structurally barred from `permission`. */
export function isPermissionForbiddenForRole(
  role: Role,
  permission: Permission,
): boolean {
  return (ROLE_FORBIDDEN_PERMISSIONS[role] ?? []).includes(permission);
}

/**
 * Drop any permission a role may never hold (ROLE_FORBIDDEN_PERMISSIONS) from
 * an arbitrary grant set — applied to the effective bundle (baseline OR
 * override) so the rule holds regardless of what an admin saved.
 */
export function sanitizePermissionsForRole(
  role: Role,
  permissions: readonly Permission[],
): Permission[] {
  const forbidden = ROLE_FORBIDDEN_PERMISSIONS[role];
  if (!forbidden || forbidden.length === 0) return [...permissions];
  const block = new Set(forbidden);
  return permissions.filter((p) => !block.has(p));
}

export function hasPermission(
  granted: readonly Permission[] | Set<Permission> | undefined,
  required: Permission,
): boolean {
  if (!granted) return false;
  if (granted instanceof Set) return granted.has(required);
  return granted.includes(required);
}

export function hasAnyPermission(
  granted: readonly Permission[] | Set<Permission> | undefined,
  required: readonly Permission[],
): boolean {
  return required.some((p) => hasPermission(granted, p));
}

/**
 * Permissions that open a section's nav tab even without its `:view` grant.
 *
 * `documentation:manage_access` is not a tier of `documentation:view` — it is
 * a different job (deciding which customer reads what, versus reading the
 * library). But its page lives inside the Documentation section, so a role
 * granted only access-management would otherwise have no tab to click and a
 * 403 on the one page it was given.
 */
const SECTION_ALT_VIEW_PERMISSIONS: Partial<Record<Section, readonly Permission[]>> = {
  documentation: ["documentation:manage_access"],
};

export function canSeeSection(
  granted: readonly Permission[] | Set<Permission> | undefined,
  section: Section,
): boolean {
  if (hasPermission(granted, SECTION_VIEW_PERMISSION[section])) return true;

  const alternates = SECTION_ALT_VIEW_PERMISSIONS[section];
  return alternates ? hasAnyPermission(granted, alternates) : false;
}

export function visibleSections(
  granted: readonly Permission[] | Set<Permission> | undefined,
): Section[] {
  return SECTIONS.filter((s) => canSeeSection(granted, s));
}

/* ── CRM granular-access helpers ────────────────────────────────────────────
 * The CRM tab layers fine-grained PAGE permissions and a single FINANCIAL data
 * class on top of the coarse `crm:view` entry gate. These are the single source
 * of truth for "which CRM pages?" and "may this user see money?" — the
 * dashboard server reads them when deriving the CRM SPA session's
 * `<page>:access` list (server/src/auth/crmSessionPermissions.ts) and the web
 * gates affordances with the same matrix. */

/** The granular CRM page-scope permissions — everything under `crm:` that gates
 * a specific page (NOT the entry gate `crm:view`, the `crm:edit` mutate grant,
 * or the `crm:view_all` data-scope widener). `crm:financials` is included: it
 * gates the financial PAGES as well as the cross-cutting money fields. Order
 * matches the CRM nav. */
export const CRM_PAGE_PERMISSIONS = [
  "crm:tasks",
  "crm:prospects",
  "crm:customers",
  "crm:contacts",
  "crm:file_manager",
  "crm:resellers",
  "crm:reseller_applications",
  "crm:licences",
  "crm:products_pricing",
  "crm:financials",
] as const satisfies readonly Permission[];

/** True when the granted set may see monetary information anywhere in the CRM.
 * The financial wall is one all-or-nothing class: hold `crm:financials` and you
 * see every dollar figure; lack it and the CRM redacts money on EVERY page.
 * super_admin always passes (its bundle is the full PERMISSIONS list). */
export function canViewCrmFinancials(
  granted: readonly Permission[] | Set<Permission> | undefined,
): boolean {
  return hasPermission(granted, "crm:financials");
}

/** True when the granted set sees EVERY CRM record, not just its own. The data
 * scope is binary: `crm:view` alone = own records (assigned to / created by
 * you); `crm:view_all` = all records tenant-wide. The brain CRM enforces the
 * actual row filter from the JWT's `crm_view_all` claim. */
export function canViewAllCrmRecords(
  granted: readonly Permission[] | Set<Permission> | undefined,
): boolean {
  return hasPermission(granted, "crm:view_all");
}

/* ── Documentation capability helpers ─────────────────────────────────────
 * The Documentation tab has three tiers (view < create < edit). These pure
 * helpers are the single source of truth for "can this user…" decisions —
 * the web UI gates affordances with them, the server proxy mirrors the create
 * vs edit split on its write routes, and the portal-api Rails backend
 * independently enforces the own-records rule (the authoritative gate). Keep
 * them here so all three layers agree. */

/** Can create NEW documents (any user with `create` or the broader `edit`). */
export function canCreateDocuments(
  granted: readonly Permission[] | Set<Permission> | undefined,
): boolean {
  return hasAnyPermission(granted, ["documentation:create", "documentation:edit"]);
}

/** Full editor: may edit/delete/publish ANY document + manage categories. */
export function canEditAllDocuments(
  granted: readonly Permission[] | Set<Permission> | undefined,
): boolean {
  return hasPermission(granted, "documentation:edit");
}

/**
 * Can this user edit/delete/publish THIS specific document? True when they are
 * a full editor (`documentation:edit`) OR they hold `documentation:create` and
 * authored the document. `authorId`/`userId` are the support-center user ids
 * (`DocAuthor.id` and `SessionUser.id`); a null author (no recorded author)
 * is never "owned" by a create-tier user.
 */
export function canModifyDocument(
  granted: readonly Permission[] | Set<Permission> | undefined,
  doc: { authorId: number | null | undefined; userId: number | null | undefined },
): boolean {
  if (canEditAllDocuments(granted)) return true;
  if (!hasPermission(granted, "documentation:create")) return false;
  return (
    doc.authorId != null && doc.userId != null && doc.authorId === doc.userId
  );
}
